//outfitComposer.ts — stacks several garment references into the one image Lucy accepts

import { COMPOSITE_WIDTH } from '../../shared/appConfig'
import type { Garment } from '../../shared/garment'
import { normaliseGarmentImage } from './garmentImageProcessor'

//Same-origin because the Worker proxies product images; that is what keeps the canvas
//readable for the projection crop below.
function loadImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image()
		image.crossOrigin = 'anonymous'
		image.decoding = 'sync'
		image.onload = () => resolve(image)
		image.onerror = () => reject(new Error(`Could not load garment image: ${url}`))
		image.src = url
	})
}

const blobToImage = async (blob: Blob): Promise<HTMLImageElement> => {
	const url = URL.createObjectURL(blob)
	try {
		return await loadImage(url)
	} finally {
		//Revoked after decode: the element keeps its own reference to the decoded bitmap.
		URL.revokeObjectURL(url)
	}
}

//Lucy carries a single reference image per prompt, so multiple garments have to be
//composited rather than sent separately. Returns null for a valid prompt-only outfit.
export async function composeOutfitReference(garments: readonly Garment[]): Promise<Blob | null> {
	const withImages = garments.filter((garment) => garment.imageURL)
	if (withImages.length === 0) return null

	const normalised: Blob[] = []
	for (const garment of withImages) {
		const source = await loadImage(garment.imageURL as string)
		const cleaned = await normaliseGarmentImage(source)
		if (cleaned) normalised.push(cleaned)
	}
	if (normalised.length === 0) return null
	//A single garment needs no canvas — its own normalised reference is already correct.
	if (normalised.length === 1) return normalised[0]

	return stack(await Promise.all(normalised.map(blobToImage)))
}

function stack(images: readonly HTMLImageElement[]): Promise<Blob | null> {
	const scaled = images.map((image) => ({
		image,
		height:
			image.naturalWidth > 0
				? Math.round((image.naturalHeight * COMPOSITE_WIDTH) / image.naturalWidth)
				: 0,
	}))
	const totalHeight = scaled.reduce((sum, entry) => sum + entry.height, 0)
	if (totalHeight === 0) return Promise.resolve(null)

	const canvas = document.createElement('canvas')
	canvas.width = COMPOSITE_WIDTH
	canvas.height = totalHeight
	const context = canvas.getContext('2d', { alpha: false })
	if (!context) return Promise.resolve(null)

	context.fillStyle = '#ffffff'
	context.fillRect(0, 0, COMPOSITE_WIDTH, totalHeight)

	//Drawn top-down in slot order, so the reference reads head-to-toe like the sentence.
	let offset = 0
	for (const entry of scaled) {
		context.drawImage(entry.image, 0, offset, COMPOSITE_WIDTH, entry.height)
		offset += entry.height
	}

	return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}
